import { useRef, useState, type Ref } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { submitApplication, type ApplicationRequest, type TimeAvail } from "./apply-form";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import {
  UserIcon,
  MailIcon,
  PhoneIcon,
  MapPinIcon,
  CheckIcon,
  CloseIcon,
} from "../../components/ui/icons";
import { cn } from "../../lib/cn";
import { BrandMark } from "../../components/ui/brand-mark";

// g-01 Guide Application（公開・Step 0 応募）。
// 応募者の基本情報・背景・稼働可能性を収集する。送信後は G-01-M01（応募完了モーダル）で
// 面接予約案内を提示する。
//
// 送信先は公開（未認証）の POST /guide-applications（tst-0xl）。受領で guide_application 行を
// 作成し、応募者へ受領＝面接案内メールを送る。実フロー: 応募 → TST がレビュー/面接 →
// 合格者を招待 → ガイドが /login → /onboarding で本登録（POST /guides・JWT 必須）。

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const TIME_OPTIONS: Array<{ key: TimeAvail; label: string; sub: string }> = [
  { key: "AM", label: "AM", sub: "8:00–13:00" },
  { key: "PM", label: "PM", sub: "13:00–20:00" },
  { key: "FullDay", label: "Full day", sub: "" },
];

// 47 都道府県の英語表記（都道府県コード順）。送信値はこの文字列そのもの（tst-szv6.22）。
const AREAS = [
  "Hokkaido",
  "Aomori",
  "Iwate",
  "Miyagi",
  "Akita",
  "Yamagata",
  "Fukushima",
  "Ibaraki",
  "Tochigi",
  "Gunma",
  "Saitama",
  "Chiba",
  "Tokyo",
  "Kanagawa",
  "Niigata",
  "Toyama",
  "Ishikawa",
  "Fukui",
  "Yamanashi",
  "Nagano",
  "Gifu",
  "Shizuoka",
  "Aichi",
  "Mie",
  "Shiga",
  "Kyoto",
  "Osaka",
  "Hyogo",
  "Nara",
  "Wakayama",
  "Tottori",
  "Shimane",
  "Okayama",
  "Hiroshima",
  "Yamaguchi",
  "Tokushima",
  "Kagawa",
  "Ehime",
  "Kochi",
  "Fukuoka",
  "Saga",
  "Nagasaki",
  "Kumamoto",
  "Oita",
  "Miyazaki",
  "Kagoshima",
  "Okinawa",
] as const;

export function Apply() {
  const [fullName, setFullName] = useState("");
  const [japaneseName, setJapaneseName] = useState("");
  const [nickname, setNickname] = useState("");
  const [area, setArea] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [intro, setIntro] = useState("");
  const [englishBg, setEnglishBg] = useState("");
  const [days, setDays] = useState<Set<string>>(new Set());
  const [time, setTime] = useState<TimeAvail | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // email 形式の判定にブラウザのネイティブ検証（validity.typeMismatch）を使うための参照（tst-eh50）。
  const emailRef = useRef<HTMLInputElement>(null);

  const toggleDay = (d: string) =>
    setDays((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });

  // 送信は <form> submit を経由しない（Button onClick）ので、必須・email 形式の検証は
  // submitApplication が明示的に行う。不正入力では post に到達せず、文言だけが返る（tst-eh50）。
  // api() を呼ぶ関数を名前付きで宣言し submit から参照する（UI 到達性の契約が名前参照でたどる）。
  const postApplication = async (body: ApplicationRequest) => {
    setError(null);
    setSubmitting(true);
    try {
      // 公開（未認証）intake。token は付けない（authHooks 未登録でも無認証で叩ける）。
      await api("/guide-applications", { method: "POST", token: null, body });
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    const result = await submitApplication(
      {
        fullName,
        japaneseName,
        nickname,
        area,
        phone,
        email,
        selfIntro: intro,
        englishBackground: englishBg,
        weekdays: [...days],
        time,
      },
      {
        emailInput: emailRef.current,
        post: postApplication,
      },
    );
    if (result.ok) setSubmitted(true);
    else setError(result.error);
  };

  return (
    <div className="min-h-dvh bg-canvas px-5 py-8">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-2 flex items-center gap-2">
          <BrandMark className="size-5 text-ink" />
          <div className="eyebrow">Tokyo Signature Tours</div>
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-ink">Guide Application</h1>
        <p className="mt-2 text-sm text-muted">Apply to become a Tokyo Signature Tours guide.</p>
        <span className="mt-3 inline-block rounded-pill bg-accent-soft px-3 py-1 text-xs font-medium text-accent">
          Step 0 · Application
        </span>

        {/* ① Basic info */}
        <Card className="mt-6 p-5">
          <SectionTitle n={1}>Basic info</SectionTitle>
          <Labeled label="Full name" required>
            <IconInput
              icon={UserIcon}
              value={fullName}
              onChange={setFullName}
              placeholder="Yamada Taro"
              autoFocus
            />
          </Labeled>
          <Labeled label="Japanese name">
            <input
              value={japaneseName}
              onChange={(e) => setJapaneseName(e.target.value)}
              placeholder="Your name in Japanese"
              className={inputClass}
            />
          </Labeled>
          <Labeled label="English nickname">
            <IconInput icon={UserIcon} value={nickname} onChange={setNickname} placeholder="Taro" />
          </Labeled>
          <Labeled label="Area of residence">
            <div className="relative">
              <MapPinIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
              <select
                value={area}
                onChange={(e) => setArea(e.target.value)}
                className={cn(inputClass, "pl-9 appearance-none", !area && "text-faint")}
              >
                <option value="">Select a prefecture</option>
                {AREAS.map((a) => (
                  <option key={a} value={a} className="text-ink">
                    {a}
                  </option>
                ))}
              </select>
            </div>
          </Labeled>
          <Labeled label="Phone">
            <IconInput
              icon={PhoneIcon}
              value={phone}
              onChange={setPhone}
              placeholder="+81 90 1234 5678"
              type="tel"
            />
          </Labeled>
          <Labeled label="Email" required>
            <IconInput
              icon={MailIcon}
              value={email}
              onChange={setEmail}
              placeholder="name@example.com"
              type="email"
              ref={emailRef}
            />
          </Labeled>
        </Card>

        {/* ② Guide background */}
        <Card className="mt-4 p-5">
          <SectionTitle n={2}>Guide background</SectionTitle>
          <Labeled label="Self introduction">
            <textarea
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={3}
              placeholder="Tell us about your guiding style and local knowledge."
              className={cn(inputClass, "h-auto py-2.5 resize-y")}
            />
          </Labeled>
          <Labeled label="English background">
            <textarea
              value={englishBg}
              onChange={(e) => setEnglishBg(e.target.value)}
              rows={3}
              placeholder="Describe your English experience."
              className={cn(inputClass, "h-auto py-2.5 resize-y")}
            />
          </Labeled>
        </Card>

        {/* ③ Availability */}
        <Card className="mt-4 p-5">
          <SectionTitle n={3}>Availability</SectionTitle>
          <div className="text-xs text-muted">Available weekdays (select all that apply)</div>
          <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-7">
            {WEEKDAYS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(d)}
                className={cn(
                  "h-11 rounded-md border text-sm font-medium transition",
                  days.has(d)
                    ? "border-ink bg-ink text-white"
                    : "border-line bg-paper text-ink hover:bg-canvas",
                )}
              >
                {d}
              </button>
            ))}
          </div>
          <div className="mt-4 text-xs text-muted">Time availability</div>
          <div className="mt-2 grid grid-cols-3 overflow-hidden rounded-md border border-line">
            {TIME_OPTIONS.map((o, i) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setTime(o.key)}
                className={cn(
                  "flex flex-col items-center justify-center py-2.5 text-sm transition",
                  i > 0 && "border-l border-line",
                  time === o.key ? "bg-ink text-white" : "bg-paper text-ink hover:bg-canvas",
                )}
              >
                <span className="font-semibold">{o.label}</span>
                {o.sub && (
                  <span
                    className={cn("text-[11px]", time === o.key ? "text-white/70" : "text-muted")}
                  >
                    {o.sub}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Card>

        {/* Next steps */}
        <Card className="mt-4 p-5">
          <h3 className="text-sm font-semibold text-ink">Next steps after applying</h3>
          <Stepper />
          <div className="mt-4 flex items-start gap-2 text-xs text-muted">
            <MailIcon className="mt-0.5 size-4 shrink-0 text-faint" />
            After submission, we&apos;ll send interview scheduling guidance by email.
          </div>
        </Card>

        {error && <p className="mt-4 text-center text-sm text-danger">{error}</p>}

        <Button size="block" className="mt-5" onClick={submit} disabled={submitting}>
          {submitting ? "Submitting…" : "Submit Application"}
        </Button>
        <p className="mt-4 text-center text-sm text-muted">
          Already applied?{" "}
          <Link to="/login" className="font-medium text-accent hover:underline">
            Sign in
          </Link>
        </p>
      </div>

      {submitted && <ApplicationSubmittedModal email={email} onClose={() => setSubmitted(false)} />}
    </div>
  );
}

const inputClass =
  "h-12 w-full rounded-md border border-line bg-paper px-3 text-[15px] text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30";

function IconInput({
  icon: Icon,
  value,
  onChange,
  placeholder,
  type = "text",
  autoFocus,
  ref,
}: {
  icon: typeof UserIcon;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
  ref?: Ref<HTMLInputElement>;
}) {
  return (
    <div className="relative">
      <Icon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
      <input
        ref={ref}
        type={type}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(inputClass, "pl-9")}
      />
    </div>
  );
}

function Labeled({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-3 first:mt-0">
      <label className="mb-1.5 block text-sm font-medium text-ink-soft">
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      {children}
    </div>
  );
}

function SectionTitle({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-ink">
      <span className="flex size-6 items-center justify-center rounded-full bg-accent text-xs font-bold text-white">
        {n}
      </span>
      {children}
    </h3>
  );
}

// 応募後の流れ（g-01 下部）。Application → Orientation → Interview → Contract(G-02)。
function Stepper() {
  const steps = [
    { label: "Application", tag: "Current", active: true },
    { label: "Orientation materials", tag: "Next" },
    { label: "30-minute interview", tag: "Later" },
    { label: "Contract onboarding", tag: "G-02" },
  ];
  return (
    <div className="mt-4 flex items-start justify-between gap-1">
      {steps.map((s, i) => (
        <div key={s.label} className="flex flex-1 flex-col items-center text-center">
          <div className="flex w-full items-center">
            <span className={cn("h-px flex-1", i === 0 ? "bg-transparent" : "bg-line")} />
            <span
              className={cn(
                "flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
                s.active ? "border-accent bg-paper text-accent" : "border-line bg-paper text-faint",
              )}
            >
              {i + 1}
            </span>
            <span
              className={cn("h-px flex-1", i === steps.length - 1 ? "bg-transparent" : "bg-line")}
            />
          </div>
          <div className="mt-1.5 text-[11px] leading-tight text-ink">{s.label}</div>
          <span
            className={cn(
              "mt-1 rounded-pill px-2 py-0.5 text-[10px] font-medium",
              s.active ? "bg-accent-soft text-accent" : "bg-canvas text-muted",
            )}
          >
            {s.tag}
          </span>
        </div>
      ))}
    </div>
  );
}

// G-01-M01 応募完了モーダル（面接予約案内）。
function ApplicationSubmittedModal({ email, onClose }: { email: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-lg bg-paper p-6 shadow-card">
        <div className="flex items-center justify-between">
          <span className="flex size-10 items-center justify-center rounded-full bg-success-soft text-success">
            <CheckIcon className="size-5" />
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-faint hover:text-ink"
            aria-label="Close"
          >
            <CloseIcon className="size-5" />
          </button>
        </div>
        <h2 className="mt-4 text-lg font-bold text-ink">Application received</h2>
        <p className="mt-2 text-sm text-muted">
          Thanks for applying. We&apos;ll review your application and send interview scheduling
          guidance to <span className="font-medium text-ink-soft">{email || "your email"}</span>{" "}
          shortly.
        </p>
        <ol className="mt-4 space-y-2 text-sm text-ink-soft">
          <li className="flex gap-2">
            <span className="text-accent">1.</span> Review orientation materials we send by email.
          </li>
          <li className="flex gap-2">
            <span className="text-accent">2.</span> Attend a 30-minute online interview.
          </li>
          <li className="flex gap-2">
            <span className="text-accent">3.</span> Once accepted, sign in to complete contract
            onboarding.
          </li>
        </ol>
        <Button size="block" className="mt-6" onClick={onClose}>
          Got it
        </Button>
        <p className="mt-3 text-center">
          <Link to="/login" className="text-sm font-medium text-accent hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
